import { MINUTES_PER_DAY, dayExtent, type MinuteRange } from '@reditelji/shared';

/**
 * Board geometry — a per-day time axis.
 *
 * Positions are percentages of the axis span rather than pixels, so the board
 * fills whatever width the grid column gives it. `MIN_PX_PER_HOUR` is the
 * density floor: below that the track scrolls instead of compressing further.
 *
 * The axis is derived from the day's own shifts and windows rather than fixed
 * at 05:00–24:00, because a shift written as running past midnight is stored as
 * two pieces and the second one starts at 00:00 (SPEC §7). On a fixed axis that
 * piece is pinned to the left edge as a sliver, on a day that otherwise ends at
 * lunchtime — which is to say invisible, on exactly the shift most likely to go
 * unstaffed.
 */

export interface Axis {
  /** Minutes from midnight at the left edge. */
  fromMin: number;
  /** Minutes from midnight at the right edge. */
  toMin: number;
  spanMin: number;
}

/** Days start early and run late; this is the axis before any data arrives. */
const FALLBACK: MinuteRange = { fromMin: 5 * 60, toMin: MINUTES_PER_DAY };

/**
 * The shortest axis worth drawing. A day stripped down to one short post would
 * otherwise stretch a 30-minute block across the whole board, which reads as a
 * full day of work.
 */
const MIN_SPAN_MIN = 6 * 60;

export const MIN_PX_PER_HOUR = 46;

/** Axis covering every range, rounded out to whole hours. */
export function makeAxis(ranges: MinuteRange[]): Axis {
  const extent = dayExtent(ranges) ?? FALLBACK;
  let fromMin = Math.max(0, Math.floor(extent.fromMin / 60) * 60);
  let toMin = Math.min(MINUTES_PER_DAY, Math.ceil(extent.toMin / 60) * 60);

  // Grow towards midnight first, then towards the start of the day: a short
  // day is almost always a morning, and padding it with afternoon reads better
  // than padding it with the small hours.
  if (toMin - fromMin < MIN_SPAN_MIN) {
    toMin = Math.min(MINUTES_PER_DAY, fromMin + MIN_SPAN_MIN);
    fromMin = Math.max(0, toMin - MIN_SPAN_MIN);
  }
  return { fromMin, toMin, spanMin: toMin - fromMin };
}

/** The default 05:00–24:00 axis, for callers with nothing to measure yet. */
export const DEFAULT_AXIS = makeAxis([]);

function clamp(axis: Axis, min: number): number {
  return Math.min(axis.toMin, Math.max(axis.fromMin, min));
}

/** Offset of a minute within the track, as a percentage of the span. */
export function minToPct(axis: Axis, min: number): number {
  return ((clamp(axis, min) - axis.fromMin) / axis.spanMin) * 100;
}

/**
 * The board's `scrollLeft` that puts a minute in the middle of the visible
 * track. Everything left of the track is the label column, which is rem-sized,
 * so it is measured (`scrollWidth - trackWidth`) rather than assumed.
 */
export function centreScrollLeft(o: {
  axis: Axis;
  min: number;
  trackWidth: number;
  clientWidth: number;
  scrollWidth: number;
}): number {
  const labelWidth = o.scrollWidth - o.trackWidth;
  const x = (minToPct(o.axis, o.min) / 100) * o.trackWidth;
  return Math.max(0, x - (o.clientWidth - labelWidth) / 2);
}

/** Width of a range, as a percentage of the span. */
export function spanPct(axis: Axis, fromMin: number, toMin: number): number {
  return ((clamp(axis, toMin) - clamp(axis, fromMin)) / axis.spanMin) * 100;
}

/** Inverse of minToPct — needs the measured track width (drag-and-drop). */
export function xToMin(axis: Axis, x: number, trackWidth: number): number {
  if (trackWidth <= 0) return axis.fromMin;
  return clamp(axis, axis.fromMin + (x / trackWidth) * axis.spanMin);
}

export function snap15(min: number): number {
  return Math.round(min / 15) * 15;
}

/** Width below which the track scrolls rather than compressing further. */
export function minTrackWidth(axis: Axis): number {
  return (axis.spanMin / 60) * MIN_PX_PER_HOUR;
}

/** Whole hours from the left edge to the right, both included. */
export function hourTicks(axis: Axis): number[] {
  return Array.from({ length: axis.spanMin / 60 + 1 }, (_, i) => axis.fromMin + i * 60);
}
